import '../../styles/dashboard-annual-system.css';
import { AthleteProgressCard } from '../../components/AthleteProgressCard';
import { displayDigits } from '../../utils/displayDigits';
import './home-workout-material.css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAthleteContext } from '../../context/AthleteContext';
import { ReadinessCard } from '../../components/ReadinessCard';
import { HomeTrendCard } from '../../components/HomeTrendCard';
import { WhoopHealthCard } from '../../components/WhoopHealthCard';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmSectionHeader } from '../../components/NmSectionHeader';
import { NmTag } from '../../components/NmTag';
import type { HomeData } from '../../services/api';
import {
  daysUntil,
  formatJalaliDateLabel,
  formatJalaliIso,
  giveDayInfo,
} from '../../utils/homeHelpers';
import { jalaliMonths } from '../../utils/jalali';
import { isConnectedWhoopHealth } from '../../utils/whoopHealth';
import { WeeklySummaryCard } from './WeeklySummaryCard';
import { MobileWorkoutRow } from './MobileWorkoutRow';
import { DashboardAiExperience } from '../../components/ai/DashboardAiExperience';
import { AddCoachModal } from '../../components/AddCoachModal';
import { useAuth } from '../../hooks/useAuth';
import type { PageId } from '../../components/Sidebar';

interface Props {
  data: HomeData;
  athleteId?: number;
  /** When false, hides the athlete AI brief (e.g. coach expand). Default true. */
  showAiBrief?: boolean;
  /** Primary-coach action for managing other coaches who share this athlete. */
  onManageAssistants?: () => void;
}

export function AthleteHomeView({ data, showAiBrief = true, onManageAssistants, athleteId }: Props) {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { effectiveUserId, selectAthlete, isCoach } = useAthleteContext();
  const targetId = athleteId ?? effectiveUserId;
  const [showAddCoach, setShowAddCoach] = useState(false);
  const todayStr = formatJalaliIso();
  const jalaliDateLabel = formatJalaliDateLabel();

  const events = data.events ?? [];
  const nextEvent = events[0];
  const upcoming = nextEvent ? daysUntil(nextEvent.event_date) > 0 : false;
  const nextDay = nextEvent ? giveDayInfo(nextEvent.event_date) : undefined;
  const eventName = nextEvent?.name ?? nextEvent?.title ?? '';
  const navigateFromAi = (page: PageId) => {
    if (page === 'charts') navigate('/charts');
    else if (page === 'calendar') navigate('/calendar');
    else navigate('/home');
  };

  return (
    <div className="athlete-home-view">
      {showAiBrief && (
        <NmSectionHeader
          className="coach-athlete-section-header"
          title={t('app.addCoach.title', 'Add coach')}
          actionLabel={t('app.addCoach.title', 'Add coach')}
          onAction={() => setShowAddCoach(true)}
        />
      )}

      <div className="athlete-home-date" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <NmTag variant="neutral">{displayDigits(jalaliDateLabel)}</NmTag>
      </div>

      {showAiBrief && (
        <div style={{ marginBottom: 12 }}>
          <DashboardAiExperience
            mode="athlete"
            viewerUserId={user?.id ?? null}
            onNavigate={navigateFromAi}
            onOpenWorkout={(workoutId, date) => {
              const params = new URLSearchParams({ workoutId: String(workoutId) });
              if (date) params.set('date', date);
              navigate(`/calendar?${params.toString()}`);
            }}
            athleteData={data}
          />
        </div>
      )}

      <AthleteProgressCard athleteId={targetId} onOpenWorkout={(id, date) => {
        if (isCoach && targetId != null) selectAthlete(targetId);
        const params = new URLSearchParams({ workoutId: String(id) });
        if (date) params.set('date', date);
        navigate(`/calendar?${params.toString()}`);
      }} />

      {data.readiness?.today && (
        <ReadinessCard
          athleteId={targetId}
          onOpenPmc={() => {
            if (isCoach && targetId != null) selectAthlete(targetId);
            navigate('/charts?chart=pmc_report');
          }}
          today={data.readiness.today}
          trends={data.today_trends ?? null}
        />
      )}

      {((data.readiness?.history?.length ?? 0) > 0 || (data.trend_daily?.length ?? 0) > 0) && (
        <div style={{ marginBottom: 12 }}>
          <HomeTrendCard
                athleteId={targetId}
                refreshKey={data}
            history={data.readiness?.history}
            daily={data.trend_daily ?? undefined}
          />
        </div>
      )}

      {isConnectedWhoopHealth(data.whoop_health) && (
        <div style={{ marginBottom: 12 }}>
          <WhoopHealthCard summary={data.whoop_health} />
        </div>
      )}

      {data.current_week_summary && (
        <WeeklySummaryCard data={data.current_week_summary} />
      )}

      <NmSectionHeader
        title={t('home.events', 'Events')}
        actionLabel={t('home.addEvent', 'Add event')}
        onAction={() => navigate(`/calendar/event/new?date=${todayStr}`)}
      />

      {nextEvent ? (
        <NmCard
          variant="raised-sm"
          style={{
            marginBottom: 12,
            padding: 14,
            cursor: 'pointer',
            background: upcoming ? undefined : 'linear-gradient(135deg, var(--vo2-cyan-500), var(--vo2-cyan-700))',
            color: upcoming ? undefined : '#fff',
          }}
          onClick={() => navigate(`/calendar/event/new?id=${nextEvent.id}`)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14 }}>{eventName}</div>
              <div style={{ fontWeight: 700, marginTop: 4 }}>
                {upcoming
                  ? t('home.daysUntil', { count: daysUntil(nextEvent.event_date), defaultValue: '{{count}} days' })
                  : t('home.eventPast', 'Finished')}
              </div>
            </div>
            {nextDay && upcoming && (
              <NmTag variant="info" className="ltr-island">
                {jalaliMonths[nextDay.JMonth - 1]} {nextDay.JDay}
              </NmTag>
            )}
          </div>
        </NmCard>
      ) : (
        <NmEmptyState>{t('home.noEvents', 'No events')}</NmEmptyState>
      )}

      <NmSectionHeader
        title={t('home.workouts', 'Workouts')}
        actionLabel={t('home.addWorkout', 'Add workout')}
        onAction={() => navigate('/calendar/workout/new')}
      />

      {data.workouts?.length ? (
        data.workouts.map((w) => (
          <MobileWorkoutRow
            key={w.id}
            workout={w}
            onPress={() => navigate(`/calendar?workout=${w.id}`)}
          />
        ))
      ) : (
        <NmEmptyState>{t('home.noWorkouts', 'No workouts today')}</NmEmptyState>
      )}

      {onManageAssistants && (
        <NmSectionHeader
          title={t('home.coach.manageSharedCoach')}
          actionLabel={t('home.coach.addSharedCoach')}
          onAction={onManageAssistants}
        />
      )}

      {showAddCoach && (
        <AddCoachModal
          isCoached={Boolean(user?.is_coached)}
          onClose={() => setShowAddCoach(false)}
          onSent={() => void refreshProfile()}
        />
      )}
    </div>
  );
}
